import React, { useState } from 'react';
import { matchPath, useLocation, Link, useParams } from 'react-router-dom';
import { classNames } from '../../../helpers/strings';
import { confs } from '../shared';
import useDeleteStudy from '../hooks/useDeleteStudy';

const Sidebar = ({ className = '' }: { className?: string }) => {
  const { pathname } = useLocation();
  const params = useParams<{ studySlug: string; conf: string }>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { deleteStudy, isDeleting } = useDeleteStudy();

  interface NavItem {
    name: string;
    href: string;
    current: boolean | undefined;
  }

  const navigation: NavItem[] = confs.map(({ name, path }) => {
    return {
      name,
      href: `/studies/${params.studySlug}/${path}`,
      current: matchPath(pathname, {
        path: `/studies/${params.studySlug}/${path}`,
        exact: true,
      })?.isExact,
    }
  })

  const handleDelete = () => {
    deleteStudy({ slug: params.studySlug });
  };

  return (
    <>
      <nav
        data-testid="sidebar"
        className={classNames('bg-white shadow-sm', className)}
      >
        <div className="w-60 shadow-md bg-white px-2.5 sticky top-24 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
          {navigation.map((item: NavItem, i) => (
            <Link
              key={`${item.name}-${i}`}
              to={item.href}
              className={classNames(
                item.current
                  ? 'border-l-4 border-indigo-500 text-gray-700 bg-gray-100 hover:text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-900',
                'w-full	flex items-center text-l my-1 p-8 h-12 overflow-hidden font-medium text-ellipsis whitespace-nowrap rounded hover:bg-gray-100 transition duration-300 ease-in-out'
              )}
              aria-current={item.current ? 'page' : undefined}
            >
              {item.name}
            </Link>
          ))}

          <div className="border-t border-gray-200 mt-4 pt-4 pb-4">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center text-l my-1 p-8 h-12 overflow-hidden font-medium text-ellipsis whitespace-nowrap rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition duration-300 ease-in-out"
            >
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Study
            </button>
          </div>
        </div>
      </nav>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowDeleteModal(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Study
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this study? This will permanently remove the study and all its configurations, strata, and associated data. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    isDeleting
                      ? 'bg-red-300 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                  }`}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Study'}
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
